import {Button, Grid, InputAdornment, Typography, useTheme} from "@mui/material";
import React, {MouseEventHandler, useContext, useEffect, useState} from "react";
import {MatrixInitializationValues} from "../../../api/types/rateDetail";
import {
    FormAutocomplete,
    FormCustomSwitch,
    FormRadioGroupWithOnChange,
    FormTextField
} from "../../../common/form-fields";
import {useTranslation} from "react-i18next";
import {useRateRepository} from "../../../api/repositories/RateRepository";
import {useRecoilState} from "recoil";
import {rateSelectListAtom, rentalPeriodsAbsoluteListAtom} from "../../../state/ratesRecoil";
import {fromRateBeToFe, i18nConvertNumberWithDecimals, setFormNumberValue} from "../../../utils";
import FileUploader from "../../../../../util/component/FileUploader";
import {RentalPeriodEntityResponse} from "../../../api/types/rentalPeriods";
import {RateSelectFE} from "../../../api/types/rate";
import CarGroupsAndRentalPeriods from "../../../common/components/CarGroupsAndRentalPeriods";
import {useFormContext} from "react-hook-form";
import {numberIntegerWithNegativeValuesRegex, numberWithNegativeValuesRegex,} from "../../../constants";
import {useRentalPeriodRepository} from "../../../api/repositories/RentalPeriodRepository";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const MatrixInitialization = ({
                                  rentalPeriods,
                                  carGroups,
                                  rateId,
                                  tpUnit,
                                  handleCancel,
                                  handleSubmit,
                              }: {
    rentalPeriods: RentalPeriodEntityResponse[];
    carGroups: string[];
    rateId: number | null;
    tpUnit: string | null;
    handleCancel: MouseEventHandler<HTMLButtonElement>;
    handleSubmit: MouseEventHandler<HTMLButtonElement>;
}) => {
    // Use Form Context
    const {watch, setValue, register, trigger, resetField} =
        useFormContext<MatrixInitializationValues>();
    const watchAllForm = watch();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Theme
    const theme = useTheme();

    // Use Recoil
    const [rateSelectList, setRateSelectList] = useRecoilState(rateSelectListAtom);
    const [rentalPeriodsAbsoluteList, setRentalPeriodsAbsoluteList] = useRecoilState(rentalPeriodsAbsoluteListAtom)

    // Use Context
    const {
        dispatchGlobalData,
        globalData: {user},
    } = useContext(GlobalContext);

    // Use State
    const [rentalPeriodsSorted, setRentalPeriodSorted] = useState<RentalPeriodEntityResponse[]>();

    // Use Repository Hooks
    const {getCloneableRates} = useRateRepository();
    const {getRentalPeriodsAbsolute} = useRentalPeriodRepository();
    const {getRentalPeriodsSorted} = useRentalPeriodRepository();
    let showOnlyActive = true;

    useEffect(() => {
        const getStaticRates = async () => {
            if (tpUnit) {
                const rateList = await getCloneableRates(
                    tpUnit,
                    showOnlyActive,
                    user.dealers
                        .map((el: any) => el.name)
                        .filter((el: any) => el !== "all"),

                    user.legalEntities
                        .map((el: any) => el.name)
                        .filter((el: any) => el !== "all")
                );
                const rateSelectList: RateSelectFE[] = fromRateBeToFe(rateList);
                setRateSelectList(rateSelectList);
            }
        };
        getStaticRates();
        const getAbsoluteRentalPeriods = async () => {
            const rentalPeriodsAbsolute: RentalPeriodEntityResponse[] = await getRentalPeriodsAbsolute();
            setRentalPeriodsAbsoluteList(rentalPeriodsAbsolute);

        }
        getAbsoluteRentalPeriods()
        const clearCarGroupsAndRentalPeriods = async () => {
            resetField("carGroups")
            resetField("rentalPeriods")
        }
        clearCarGroupsAndRentalPeriods();
        // eslint-disable-next-line
        const getRentalPeriodSorted = async () => {
            const rentalPeriodSorted: RentalPeriodEntityResponse[] = await getRentalPeriodsSorted(rentalPeriods)
            setRentalPeriodSorted(rentalPeriodSorted)
        }
        getRentalPeriodSorted()
    }, [tpUnit]);

    return (
        <Grid container spacing={2} p={2} item xs={12}>
            <Grid container spacing={2} p={2} item xs={12}>
                <Typography sx={{color: theme.palette.primary.dark}}>
                    {t("pricing:rateMatrixInsert.title")}:
                </Typography>
            </Grid>
            <Grid container spacing={2} p={2} item xs={12}>
                <Grid>
                    {/* TIPO DI INIZIALIZZAZIONE */}
                    <FormRadioGroupWithOnChange
                        {...register("matrixInitializationType")}
                        label=""
                        radioButtons={[
                            {value: 0, label: t("pricing:rateMatrixInsert.form.labels.emptyMatrix")},
                            {value: 1, label: t("pricing:rateMatrixInsert.form.labels.copyMatrix")},
                            {value: 2, label: t("pricing:rateMatrixInsert.form.labels.importExcel")}
                        ]}
                        handleChange={(newValue: string) => setValue("matrixInitializationType", Number(newValue))}
                    />
                </Grid>
                {watchAllForm.matrixInitializationType === 0 &&
                    <Grid container spacing={2} pb={2} item xs={12}>
                        <CarGroupsAndRentalPeriods carGroups={carGroups}
                                                   rentalPeriods={rentalPeriodsSorted as RentalPeriodEntityResponse[]}
                                                   rentalPeriodsAbsolute={rentalPeriodsAbsoluteList}
                                                   absolute={watch("absolute")}/>
                        {/*ABSOLUTE RATE */}
                        <Grid item xs={12} md={3}>
                            <FormCustomSwitch
                                {...register("absolute")}
                                label={t("pricing:rateMatrixInsert.form.labels.absolute")}
                                handleChange={(value: boolean) => {
                                    setValue("absolute", value)
                                    resetField('rentalPeriods');
                                }}
                            />
                        </Grid>
                    </Grid>
                }
                {watchAllForm.matrixInitializationType === 1 &&
                    <Grid container spacing={2} p={2} item xs={12} md={12}>
                        <Grid item xs={12} md={3}>
                            {/* RATE */}
                            <FormAutocomplete
                                {...register("rate")}
                                label={`${t("pricing:rate.form.labels.referenceTariffLabels.rate")}`}
                                options={rateSelectList}
                                isOptionEqualToValue={(option, value) => option.code === value.code}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            {/* DISCOUNT INCREASE PERCENTAGE */}
                            <FormTextField
                                {...register("discountIncreasePercentage")}
                                label={`${t("pricing:rate.form.labels.referenceTariffLabels.discountIncreasePercentage")}`}
                                readOnly={!watchAllForm.rate}
                                type={"text"}
                                handleChange={(value: string) => {
                                    setFormNumberValue(value, setValue, "discountIncreasePercentage", numberIntegerWithNegativeValuesRegex)
                                    // if (numberIntegerRegex.test(value.trim())) {
                                    //     setValue("discountIncreaseFixed", null);
                                    //     trigger("discountIncreaseFixed")
                                    // }
                                }}
                                startAdornment={<InputAdornment position="start">%</InputAdornment>}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            {/* DISCOUNT INCREASE FIXED */}
                            <FormTextField
                                {...register("discountIncreaseFixed")}
                                label={`${t("pricing:rate.form.labels.referenceTariffLabels.discountIncreaseFixed")}`}
                                readOnly={!watchAllForm.rate}
                                type={"text"}
                                handleChange={(value: string) => {
                                    setFormNumberValue(i18nConvertNumberWithDecimals(value), setValue, "discountIncreaseFixed", numberWithNegativeValuesRegex);
                                    // if (numberIntegerRegex.test(value.trim())) {
                                    //     setValue("discountIncreasePercentage", null);
                                    //     trigger("discountIncreasePercentage")
                                    // }
                                }}
                            />
                        </Grid>
                    </Grid>
                }
                {watchAllForm.matrixInitializationType === 2 &&
                    <Grid container spacing={2} p={2} item xs={12}>
                        <Grid item xs={12} md={3}>
                            <FileUploader onUpload={(file: File) => setValue("excelFile", file)}
                                          onRemove={() => setValue("excelFile", null)}
                                          acceptedFiles={{"text/xls": [".xls", ".xlsx"]}}/>
                        </Grid>
                    </Grid>
                }
            </Grid>
            <Grid container item xs={12} justifyContent="end" columnGap={2}>
                {rateId && <Button color={"accent"}
                                   type={"button"}
                                   variant={"outlined"}
                                   onClick={handleCancel}
                >
                    {t("pricing:actions.cancel")}
                </Button>}
                <Button color={"accent"}
                        type={"button"}
                        disabled={watchAllForm.matrixInitializationType === 2 && Boolean(!watchAllForm.excelFile)}
                        onClick={handleSubmit}
                >
                    {t("pricing:actions.confirm")}
                </Button>
            </Grid>
        </Grid>
    );
};
export default MatrixInitialization;